import { Injectable } from "@angular/core";
import { FeatureFlagService } from "../../../../util/feature-flag.service";
import { ProductV2Service } from "./product.v2.service";
import { ProductV3Service } from "./product.v3.service";
import { Observable, map } from 'rxjs';
import { ProductListVMResponse } from "../../../../models/product-v3";
import { ProductDetailMapper } from "../mappers/product-detail.mapper";
import { ProductListMapper } from "../mappers/product-list.mapper";
import { AddSpecifications } from "../../../../models/specifications";
import { CreateOrEditBranchPrice } from "../../../../models/product-v2";

@Injectable({ providedIn: "root" })
export class ProductService {
  constructor(
    private v2Service: ProductV2Service,
    private v3Service: ProductV3Service,
    private featureFlag: FeatureFlagService
  ) {}

  /* =======================
   * READ
   ======================= */

  list(storeId: string, filters: any): Observable<ProductListVMResponse> {
    const isV2 = this.featureFlag.isV2Store(storeId);

    const request$: Observable<any> = isV2
      ? this.v2Service.list(storeId, filters)
      : this.v3Service.list(storeId, filters);

    return request$.pipe(
      map((res) => ProductListMapper.toVMResponse(res, isV2))
    );
  }

  get(storeId: string, productId: string) {
    const isV2 = this.featureFlag.isV2Store(storeId);
    const request$: Observable<any> = isV2
      ? this.v2Service.get(storeId, productId)
      : this.v3Service.get(storeId, productId);

    return request$.pipe(map((res) => ProductDetailMapper.toVM(res, isV2)));
  }

  getProductWithSkus(storeId: string, productId: string) {
    // ⚠️ Solo existe en V3
    return this.v3Service.getProductWithSkus(storeId, productId);
  }
  updateFile(storeId: string, file: FormData) {
    // ⚠️ Solo existe en V2
    return this.v2Service.updateFile(storeId, file);
  }
  uploadRelated(storeId: string, file: FormData) {
    // ⚠️ Solo existe en V2
    return this.v2Service.uploadRelated(storeId, file);
  }
  uploadStock(storeId: string, file: FormData) {
    // ⚠️ Solo existe en V2
    return this.v2Service.uploadStock(storeId, file);
  }
  deletePrice(storeId: string, productId: string, priceId: string) {
    // ⚠️ Solo existe en V2
    return this.v2Service.deletePrice(storeId, productId, priceId);
  }
  addSpecifications(
    specifications: AddSpecifications[],
    storeId: string,
    productId: string
  ) {
    // ⚠️ Solo existe en V2
    return this.v2Service.addSpecifications(specifications, storeId, productId);
  }
  addPrices(
    storeId: string,
    productId: string,
    body: CreateOrEditBranchPrice[]
  ) {
    // ⚠️ Solo existe en V2
    return this.v2Service.addPrices(storeId, productId, body);
  }

  /* =======================
   * CREATE
   ======================= */

  create( payload: any,storeId: string,) {
    return this.featureFlag.isV2Store(storeId)
      ? this.v2Service.create(payload, storeId)
      : this.v3Service.create( payload,storeId);
  }

  createPack(storeId: string, payload: any) {
    // ⚠️ Solo V2 (bundles)
    return this.v2Service.createPack(payload, storeId);
  }

  /* =======================
   * UPDATE
   ======================= */

  update(storeId: string, productId: string, payload: any) {
    console.log("storeId", storeId);
    return this.featureFlag.isV2Store(storeId)
      ? this.v2Service.update(payload, storeId, productId)
      : this.v3Service.update(storeId, productId, payload);
  }

  /* =======================
   * DELETE
   ======================= */

  delete(storeId: string, productId: string) {
    this.v3Service.delete(storeId, productId);
  }

  /* =======================
   * ENABLE / DISABLE
   ======================= */

  enable(storeId: string, productIds: string | string[]) {
    if (this.featureFlag.isV2Store(storeId)) {
      return this.v2Service.enable(
        storeId,
        Array.isArray(productIds) ? productIds : [productIds]
      );
    }

    // V3 → uno solo
    const id = Array.isArray(productIds) ? productIds[0] : productIds;
    return this.v3Service.enable(storeId, id);
  }

  disable(storeId: string, productIds: string | string[]) {
    if (this.featureFlag.isV2Store(storeId)) {
      return this.v2Service.disable(
        storeId,
        Array.isArray(productIds) ? productIds : [productIds]
      );
    }

    const id = Array.isArray(productIds) ? productIds[0] : productIds;
    return this.v3Service.disable(storeId, id);
  }

  /* =======================
   * STOCK / PRICES (V2 ONLY)
   ======================= */

  addStock(storeId: string, productId: string, body: any) {
    return this.v2Service.addStock(storeId, productId, body);
  }

  getProductPrices(storeId: string, productId: string) {
    return this.v2Service.getProductPrices(storeId, productId);
  }

  /* =======================
   * BUNDLES (V2 ONLY)
   ======================= */

  addItemsToBundle(storeId: string, productId: string, items: any[]) {
    return this.v2Service.addItemsToBundle(storeId, productId, items);
  }

  /* =======================
   * FILES / EXCEL (V2 ONLY)
   ======================= */

  upload(storeId: string, file: FormData) {
    return this.v2Service.upload(storeId, file);
  }
}
